import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../context/CookieConsentContext';

const categories = ['necessary', 'functional', 'analytics', 'marketing'];

const CookieBanner = () => {
  const { t } = useTranslation();
  const {
    consent,
    ready,
    hasConsented,
    acceptAll,
    rejectAll,
    saveCustom,
    settingsOpen,
    openSettings,
    closeSettings,
  } = useCookieConsent();

  const [prefs, setPrefs] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
  });

  React.useEffect(() => {
    if (consent) {
      setPrefs({
        necessary: true,
        functional: Boolean(consent.functional),
        analytics: Boolean(consent.analytics),
        marketing: Boolean(consent.marketing),
      });
    }
  }, [consent, settingsOpen]);

  if (!ready) return null;

  const showBanner = !hasConsented && !settingsOpen;

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[80] bg-[#242424] text-white px-5 md:px-12 py-6 shadow-[0_-8px_30px_rgba(0,0,0,0.25)]">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h3 className="text-20 font-medium mb-2">{t('cookie.bannerTitle')}</h3>
              <p className="text-14 font-light text-white/80 leading-relaxed">
                {t('cookie.bannerText')}{' '}
                <Link to="/cerez-politikasi" className="underline hover:text-white">
                  {t('footer.cookiePolicy')}
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={rejectAll}
                className="border border-white/50 px-6 py-2.5 text-14 font-light hover:bg-white hover:text-[#242424] duration-200"
              >
                {t('cookie.reject')}
              </button>
              <button
                type="button"
                onClick={openSettings}
                className="border border-white/50 px-6 py-2.5 text-14 font-light hover:bg-white hover:text-[#242424] duration-200"
              >
                {t('cookie.settings')}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="bg-white text-[#242424] px-6 py-2.5 text-14 font-medium hover:opacity-90 duration-200"
              >
                {t('cookie.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasConsented && !settingsOpen && (
        <button
          type="button"
          onClick={openSettings}
          className="fixed bottom-5 left-5 z-[70] bg-[#242424] text-white text-12 font-light px-4 py-2 border border-white/20 hover:bg-black duration-200"
        >
          {t('footer.cookieSettings')}
        </button>
      )}

      {settingsOpen && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-end justify-center p-0">
          <div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6 md:p-8 mb-0 md:mb-8 md:mx-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-24 font-medium">{t('cookie.settingsTitle')}</h3>
              <button type="button" onClick={closeSettings} className="text-20 leading-none px-2">
                ×
              </button>
            </div>
            <p className="text-14 font-light text-customGray mb-6">{t('cookie.settingsText')}</p>

            <div className="space-y-5 mb-8">
              {categories.map((key) => (
                <div key={key} className="border border-black/10 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h4 className="text-16 font-medium">{t(`cookie.categories.${key}.title`)}</h4>
                      <p className="text-14 font-light text-customGray mt-1">
                        {t(`cookie.categories.${key}.desc`)}
                      </p>
                    </div>
                    <label className="shrink-0">
                      <input
                        type="checkbox"
                        checked={key === 'necessary' || key === 'functional' ? true : prefs[key]}
                        disabled={key === 'necessary' || key === 'functional'}
                        onChange={(e) =>
                          setPrefs((prev) => ({ ...prev, [key]: e.target.checked }))
                        }
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {consent?.consentId && (
              <p className="text-12 font-light text-customGray mb-6">
                {t('cookie.consentRecord', {
                  id: consent.consentId,
                  date: new Date(consent.timestamp).toLocaleString(),
                })}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={rejectAll}
                className="border border-black/40 px-6 py-2.5 text-14 font-light"
              >
                {t('cookie.reject')}
              </button>
              <button
                type="button"
                onClick={() => saveCustom(prefs)}
                className="border border-black/40 px-6 py-2.5 text-14 font-light"
              >
                {t('cookie.save')}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="bg-[#242424] text-white px-6 py-2.5 text-14 font-medium"
              >
                {t('cookie.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
